import * as Popover from '@radix-ui/react-popover'
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled, { css, keyframes } from 'styled-components'
import { openingStatusText } from '../../core/application/utils/scheduleUtils'
import DownChevronIcon from '../icons/DownChevronIcon'

export interface IScheduleOpeningHours {
  opening: string
  closing: string
}

export interface IScheduleDropdownDay {
  day: string
  openingHours: IScheduleOpeningHours[]
}

export interface IScheduleDropdownProps {
  days: Array<IScheduleDropdownDay>
  isOpenNow: boolean
}

const ScheduleDropdown: React.FC<IScheduleDropdownProps> = ({ days, isOpenNow }) => {
  const intl = useIntl()
  return (
    <Popover.Root>
      <Trigger>
        <div>{openingStatusText({ days, isOpenNow }, intl)}</div>
        <DownChevronIcon />
      </Trigger>
      <Container>
        <Wrapper>
          {days.map((day, index) => (
            <ScheduleItem key={index}>
              <ScheduleItemMainLine key={'item-0'}>
                <div className="day">
                  <FormattedMessage id={`ScheduleDropdown.Day.${day.day}.Label`} />
                </div>
                {day.openingHours.length > 0 ? (
                  <div className="hour">
                    {day.openingHours[0].opening} - {day.openingHours[0].closing}
                  </div>
                ) : (
                  <div className="closed">
                    <FormattedMessage id="ScheduleDropdown.State.Closed" />
                  </div>
                )}
              </ScheduleItemMainLine>
              {day.openingHours.length > 1 &&
                day.openingHours.map((hours, index) => {
                  if (index > 0) {
                    return (
                      <ScheduleItemAdditionalLine key={`item-${index}`}>
                        <div className="hour">
                          {hours.opening} - {hours.closing}
                        </div>
                      </ScheduleItemAdditionalLine>
                    )
                  }
                  return <></>
                })}
            </ScheduleItem>
          ))}
        </Wrapper>
      </Container>
    </Popover.Root>
  )
}

const slideIn = keyframes({
  from: { transform: 'translateY(2px)', opacity: 0 },
  to: { transform: 'translateY(0)', opacity: 1 }
})

const Trigger = styled(Popover.Trigger)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  justify-content: center;
  align-items: center;

  svg {
    transition: transform 300ms;
  }

  &[data-state='open'] svg {
    transform: rotate(180deg);
  }
`

const Container = styled(Popover.Content)`
  background-color: ${({ theme }) => theme.color.background};
  min-width: 200px;
  border-radius: 4px;
  padding: 10px;
  box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.1);

  &[data-state='open'] {
    animation: ${() => css`
      ${slideIn} 500ms cubic-bezier(0.16, 1, 0.3, 1)
    `};
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 5px;
`

const ScheduleItem = styled.div`
  display: flex;
  flex-direction: column;
`

const ScheduleItemMainLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  font-size: ${({ theme }) => theme.size.smaller};
  color: ${({ theme }) => theme.color.black};
`

const ScheduleItemAdditionalLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;

  font-size: ${({ theme }) => theme.size.smaller};
  color: ${({ theme }) => theme.color.black};
`

export default ScheduleDropdown
